/**
 * Серверное состояние стартового предложения набора тем (#845, PR 4).
 *
 * Чтение (`GET …/bootstrap`) ничего не запускает: генерация — только кнопками
 * «Предложить темы по истории» и «Предложить заново». Пока предложение
 * готовится (`running`), состояние перечитывается раз в 5 секунд — генерация
 * длится до пары минут, а ответ кнопки мог вернуться раньше её конца. Брошенная
 * попытка (`stale`: старше окна, которое сервер готов перезахватить) не ждётся —
 * опрос прекращается, экран предлагает кнопку.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApproveThemeBootstrapRequest,
  ApproveThemeBootstrapResponse,
} from '@aica/api-types/schemas';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { retryTransientOnly } from '@/lib/query-retry';
import { invalidateVoiceThemeAnalytics, refreshVoiceThemeList } from './useVoiceThemes';

export const themeBootstrapKeys = {
  state: (orgId: string | null) => ['analytics', 'voice-theme-bootstrap', orgId] as const,
};

/** Как часто перечитывать, пока предложение готовится. */
export const THEME_BOOTSTRAP_POLL_MS = 5_000;

export function useThemeBootstrap(options: { enabled: boolean }) {
  const { apiClient, orgId } = useAuth();
  return useQuery({
    queryKey: themeBootstrapKeys.state(orgId),
    queryFn: () => apiClient.voiceThemes.bootstrapState(),
    enabled: Boolean(orgId) && options.enabled,
    staleTime: 30 * 1000,
    retry: retryTransientOnly,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.status === 'running' && !data.stale ? THEME_BOOTSTRAP_POLL_MS : false;
    },
  });
}

export function useGenerateThemeBootstrap() {
  const { apiClient, orgId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mode: 'start' | 'regenerate') =>
      apiClient.voiceThemes.bootstrapGenerate(mode),
    // The answer IS the state after the press — no second read.
    onSuccess: (state) =>
      queryClient.setQueryData(themeBootstrapKeys.state(orgId), state),
    // A press that failed may still have claimed the attempt: the client gave
    // up on a slow answer (timeout) or edge-api answered 503 after the claim.
    // The generation then runs on unseen — re-read the state, and the screen
    // polls it as `running` instead of showing the pre-press state.
    onError: () =>
      queryClient.invalidateQueries({ queryKey: themeBootstrapKeys.state(orgId) }),
  });
}

/**
 * Утверждение меняет и набор, и предложение (пары стёрты): перечитываются оба;
 * аналитика тем — только если утверждение создало тему.
 * `onApproved` вызывается на успехе ДО перечитывания: экран утверждения после
 * него исчезает, а итог («добавлено N, не поместилось M») должен остаться у
 * того, кто не размонтируется вместе с ним.
 */
export function useApproveThemeBootstrap(
  onApproved?: (response: ApproveThemeBootstrapResponse) => void
) {
  const { apiClient, orgId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ApproveThemeBootstrapRequest) =>
      apiClient.voiceThemes.bootstrapApprove(body),
    onSuccess: (response) => {
      onApproved?.(response);
      // The set changed only if a theme was created; all-duplicate is a no-op.
      if (response.results.some((result) => result.status === 'created')) {
        invalidateVoiceThemeAnalytics(queryClient, orgId);
      }
    },
    onSettled: () =>
      Promise.all([
        refreshVoiceThemeList(queryClient, orgId),
        queryClient.invalidateQueries({ queryKey: themeBootstrapKeys.state(orgId) }),
      ]),
  });
}
